import React, { useState, useEffect, useMemo } from 'react';
import { X, Calendar, Users, Clock, MapPin, Mail, FileText, User, UserCheck } from 'lucide-react';
import { toast } from 'react-toastify';
import useAxiosInstance from '../../lib/useAxiosInstance';
import { useQuery,useQueryClient, useMutation } from 'react-query';
import { Row, Col, Select, Form } from 'antd';
import BlockCalendarModal from './BlockCalendarModal';
// import "../../Styles/eventScheduler.css"

const CreateEventModal = ({ 
  isOpen, 
  onClose, 
  onCreateEvent, 
  selectedDate = '', 
  // selectedTime = '' 
  selectedStartTime  = '',
  selectedEndTime    = '',
  dndEntries,
  onDNDCreated,
  initialTab = 'visit' 
}) => {
  const axiosInstance = useAxiosInstance();
  const user = JSON.parse(localStorage.getItem("user"));
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showTimeSelector, setShowTimeSelector] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  // const [attendeesList, setAttendeesList] = useState([]);
  const [formData, setFormData] = useState({
    eventTitle: '',
    typeOfEvent: 'visit', // maps to 'visit', 'invitation', or 'appointment'
    visitorType: '',
    appointmentType: '',
    invitationType: '',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    phone: '',
    description: '',
    attendees: [], // should be an array of selected user IDs
    notes: '',
    startTime: selectedStartTime,
    endTime: selectedEndTime,
    date: selectedDate,
  });

  useEffect(() => {
  if (isOpen) {
    setActiveTab(initialTab);
    setFormData(prev => ({ ...prev, typeOfEvent: initialTab }));
  }
}, [isOpen, initialTab]);

const createEvent = async (payload) => {
  const res = await axiosInstance.post('api/eventScheduler/events', payload);
  if (!res?.data?.status) throw new Error(res?.data?.message || 'Failed to create event');
  return res.data.data;
};

const { mutateAsync: createEventMutation, isLoading: isCreatingEvent } = useMutation(createEvent, {
  onSuccess: (data) => {
    toast.success("Event Scheduled Successfully");
    onCreateEvent && onCreateEvent(data);
    onClose();
    queryClient.invalidateQueries("Event-List"); 
  },
  onError: (err) => {
    toast.error(err?.message || 'Failed to create event');
  }
});

const { mutateAsync: addDND } = useMutation(
  async (dndData) => {
    const res = await axiosInstance.post('api/eventScheduler/addDND', dndData);
    return res.data;
  },
  {
    onSuccess: () => {
      toast.success('Calendar blocked successfully!');
      setIsBlockModalOpen(false);
      if (onDNDCreated) onDNDCreated(); // refetch
    },
    onError: (error) => {
      toast.error(error?.message || 'Failed to block calendar');
    }
  }
);

const {
  data: attendeesList = [],       // default to []
  isLoading: attendeesLoading,
  isError: attendeesError,
  refetch: refetchAttendees,
} = useQuery(
  ["Attendees-List"],           
  async () => {
    const res = await axiosInstance.get("api/eventScheduler/attendees");
    if (!res?.data?.status) throw new Error(res?.data?.message);
    return res.data.data || [];
  },
  {
    retry: 1,
    refetchOnWindowFocus: false,
  }
);

useEffect(() => {
  if (isOpen) {
    document.body.classList.add("overflow-hidden");
  } else {
    document.body.classList.remove("overflow-hidden");
  }

  return () => document.body.classList.remove("overflow-hidden");
}, [isOpen]);


 useEffect(() => {
  setFormData(prev => ({
    ...prev,
    date:      selectedDate,
    startTime: selectedStartTime,
    endTime:   selectedEndTime || prev.endTime,   // keep previous if no drag‑range
  }));

  /* auto‑show the time controls if a start time exists */
  setShowTimeSelector(!!selectedStartTime);

  /* if user only single‑clicked (no endTime), default to +1 h */
  if (selectedStartTime && !selectedEndTime) {
    const [h, m]   = selectedStartTime.split(':').map(Number);
    const endHours = (h + 1) % 24;
    setFormData(prev => ({
      ...prev,
      endTime: `${endHours.toString().padStart(2, '0')}:${m
        .toString()
        .padStart(2, '0')}`,
    }));
  }
}, [selectedDate, selectedStartTime, selectedEndTime]);

  if (!isOpen) return null;

  const handleClose = () => {
  setFormData({
    eventTitle: '',
    date: '',
    startTime: '',
    endTime: '',
    duration: '60',
    location: '',
    description: '',
    attendees: '',
    typeOfEvent: 'visit',
    visitorType: '',
    appointmentType: '',
    invitationType: '',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    phone: '',
    notes: ''
  });
  setShowTimeSelector(false);
  setActiveTab('visit');
  onClose();
};
  
const handleBlockCalendarSave = async (dndData) => {
  await addDND(dndData);
};

  const isTimeBlocked = (startTime, endTime) => {
    const eventStart = new Date(startTime);
    const eventEnd = new Date(endTime);

    return dndEntries.some(dnd => {
      const dndStart = new Date(dnd.start);
      const dndEnd = new Date(dnd.end);

      return (
        (eventStart >= dndStart && eventStart < dndEnd) ||
        (eventEnd > dndStart && eventEnd <= dndEnd) ||
        (eventStart <= dndStart && eventEnd >= dndEnd)
      );
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {

        const start = showTimeSelector
        ? new Date(`${formData.date}T${formData.startTime}:00`).toISOString()
        : new Date(`${formData.date}T00:00:00`);

        const end = showTimeSelector
        ? new Date(`${formData.date}T${formData.endTime}:00`).toISOString()
        : new Date(`${formData.date}T23:59:59`);

        // Check if the selected time is blocked
        if (isTimeBlocked(start, end)) {
          toast.error('The selected date and time is blocked. Please choose a different time.');
          return;
        }
        // Validate start time is before end time
        if (new Date(start) >= new Date(end)) {
          toast.error('Start time must be before end time');
          return;
        }

        const payload = {
        eventTitle: formData.eventTitle,
        visitorType: formData.visitorType,
        appointmentType: formData.appointmentType,
        invitationType: formData.invitationType,
        firstName: formData.firstName,
        lastName: formData.lastName,
        address: formData.address,
        email: formData.email,
        phone: formData.phone,
        description: formData.description,
        attendees:formData.attendees,
        notes: formData.notes,
        typeOfEvent: formData.typeOfEvent,
        start,
        end,
        };

        await createEventMutation(payload);

        setFormData({
        eventTitle: '',
        date: '',
        startTime: '',
        endTime: '',
        duration: '60',
        location: '',
        description: '',
        attendees: '',
        typeOfEvent: 'visit',
        visitorType: '',
        appointmentType: '',
        invitationType: '',
        firstName: '',
        lastName: '',
        address: '',
        email: '',
        phone: '',
        notes: ''
        });

        setShowTimeSelector(false);
    } catch (error) {
        console.error(error);
        toast.error(error?.response?.data?.message || 'An error occurred while creating the event');
    }
};

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const tabs = [
    { id: 'visit', label: 'Visit', icon: <MapPin className="w-4 h-4" />, color: 'text-green-600' },
    { id: 'invitation', label: 'Invitation', icon: <Mail className="w-4 h-4" />, color: 'text-purple-600' },
    { id: 'appointment', label: 'Appointment', icon: <Calendar className="w-4 h-4" />, color: 'text-blue-600' }
  ];

  const getSelectedAttendees = () => {
    return attendeesList.filter((attendee) => formData.attendees.includes(attendee._id))
  }

  // Custom option renderer for the dropdown
  const renderOption = (attendee) => (
    <div className="flex items-center justify-between py-2 px-1">
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-gradient-to-r from-[#004877] to-purple-200 rounded-full flex items-center justify-center">
          <span className="text-white text-sm font-medium">
            {attendee.firstName?.charAt(0)?.toUpperCase()}
            {attendee.lastName?.charAt(0)?.toUpperCase()}
          </span>
        </div>
        <div>
          <div className="font-medium text-gray-900">
            {attendee.firstName} {attendee.lastName || ""}
          </div>
          <div className="text-sm text-gray-500">{attendee.role || "Staff Member"}</div>
        </div>
      </div>
    </div>
  )

  const inviteeTypes = [
  "Public Events",
  "Government & Institutional",
  "Cultural & Religious",
  "Social Functions",
  "Organizational / Party Related",
  "Business & Community",
  "Media & Press",
  "Other"
];

const appointmentTypes = [
  "Public Grievance Meeting",
  "Personal Meeting Request",
  "Political Discussion",
  "Media Interaction",
  "Government Official Visit",
  "Party Worker/Volunteer Meeting",
  "Organizational Meeting",
  "Delegation/Group Appointment",
  "Business/Investor Meeting",
  "Civil Society/NGO Interaction",
  "Follow-up Appointment",
  "Courtesy Visit"
];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm  flex items-center justify-center z-50 p-4 overflow-hidden">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold heading-white">Schedule Event</h2>
            <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] bg-white">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Tabs */}
            <div className="flex space-x-1 modal-tab-bg rounded-lg p-1">
              {tabs.map((tab) => (
                
                <button
                    key={tab.id}
                    type="button"
                    onClick={() => {
                        setActiveTab(tab.id);
                        setFormData((prev) => ({ ...prev, typeOfEvent: tab.id }));
                    }}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 flex-1 justify-center text-sm font-medium ${
                        activeTab === tab.id
                        ? 'modal-tab shadow-sm text-gray-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                    >
                    <span className={activeTab === tab.id ? tab.color : 'text-gray-500'}>
                        {tab.icon}
                    </span>
                    <span className='heading-white'>{tab.label}</span>
                </button>
                
              ))}
              {user?.role !=='data_entry_operator' && <button
                  type="button"
                  onClick={() => {setIsBlockModalOpen(true)}}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all duration-200 flex-1 justify-center text-sm font-medium ${
                    'text-red-600 hover:bg-red-50 border border-red-100'
                  }`}
                >
                  <Calendar className="w-4 h-4" />
                  <span>Block Calendar</span>
              </button>}
            </div>

            <Row gutter={16}>
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">Event Title</label>
                <input
                  type="text"
                  name="eventTitle"
                  value={formData.eventTitle}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                  placeholder={`Add ${activeTab} title`}
                />
              </Col>
              <Col span={9}>
                <label className="block text-sm font-medium subheading mb-1">
                  {formData.typeOfEvent === "visit" && "Visitor Type"}
                  {formData.typeOfEvent === "invitation" && "Invitee Type"}
                  {formData.typeOfEvent === "appointment" && "Appointment Type"}
                </label>
                <Select
                  placeholder={`Select ${formData.typeOfEvent === 'visit' ? 'Visitor' : formData.typeOfEvent === 'invitation' ? 'Invitee' : 'Appointment'} Type`}
                  size="large"
                  name="visitorType"
                  value={formData.visitorType}
                  onChange={(value) => setFormData({ ...formData, visitorType: value })}
                  options={
                    formData.typeOfEvent === "visit"
                      ? [
                          { value: "citizen", label: "Citizen" },
                          { value: "vip", label: "VIP" },
                          { value: "cadre", label: "Cadre" },
                          { value: "other", label: "Other" }
                        ]
                      : formData.typeOfEvent === "invitation"
                      ? [
                          { value: "Public Events", label: "Public Events" },
                          { value: "Government & Institutional", label: "Government & Institutional" },
                          { value: "Cultural & Religious", label: "Cultural & Religious" },
                          { value: "Social Functions", label: "Social Functions" },
                          { value: "Organizational / Party Related", label: "Organizational / Party Related" },
                          { value: "Business & Community", label: "Business & Community" },
                          { value: "Media & Press", label: "Media & Press" },
                          { value: "Other", label: "Other" }
                        ]
                      : [
                          { value: "Public Grievance Meeting", label: "Public Grievance Meeting" },
                          { value: "Personal Meeting Request", label: "Personal Meeting Request" },
                          { value: "Political Discussion", label: "Political Discussion" },
                          { value: "Media Interaction", label: "Media Interaction" },
                          { value: "Government Official Visit", label: "Government Official Visit" },
                          { value: "Party Worker/Volunteer Meeting", label: "Party Worker/Volunteer Meeting" },
                          { value: "Organizational Meeting", label: "Organizational Meeting" },
                          { value: "Delegation/Group Appointment", label: "Delegation/Group Appointment" },
                          { value: "Business/Investor Meeting", label: "Business/Investor Meeting" },
                          { value: "Civil Society/NGO Interaction", label: "Civil Society/NGO Interaction" },
                          { value: "Follow-up Appointment", label: "Follow-up Appointment" },
                          { value: "Courtesy Visit", label: "Courtesy Visit" }
                        ]
                  }
                />
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">Date</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                  className=" w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
              {!showTimeSelector ? (
                <button
                  type="button"
                  onClick={() => setShowTimeSelector(true)}
                  className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors text-sm font-medium"
                >
                  Add time
                </button>
              ) : (
                <Col span={12}>
                  <label className="block text-sm font-medium subheading mb-1">Time</label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="time"
                      name="startTime"
                      value={formData.startTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                    />
                    <span className="text-gray-400">–</span>
                    <input
                      type="time"
                      name="endTime"
                      value={formData.endTime}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                    />
                  </div>
                    
                </Col>
              )}
            </Row>
            <Row gutter={16} className="mt-4">
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">First Name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">Last Name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
            </Row>
            <Row gutter={16} className="mt-4">
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">Phone</label>
                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
            </Row>
            {/* <Row gutter={24} className="mt-4"> */}
              {["invitation", "appointment"].includes(formData.typeOfEvent) && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium subheading mb-2">
                    {/* <Users className="w-4 h-4 inline mr-2" /> */}
                    {formData.typeOfEvent==='invitation' && (`Select Invitees`)}
                    {formData.typeOfEvent==='appointment' && (`Select Attendees`)}
                  </label>
                  <Select
                    mode="multiple"
                    style={{ width: "100%" }}
                    size="large"
                    placeholder="Choose attendees for this event"
                    value={formData.attendees}
                    onChange={(value) => setFormData({ ...formData, attendees: value })}
                    optionLabelProp="label"
                    className="custom-select"
                    dropdownStyle={{ padding: "8px" }}
                  >
                    {attendeesList.map((attendee) => (
                      <Select.Option
                        key={attendee._id}
                        value={attendee._id}
                        label={`${attendee.firstName} ${attendee.lastName || ""}`}
                      >
                        {renderOption(attendee)}
                      </Select.Option>
                    ))}
                  </Select>
                </div>

                {/* Selected Attendees Cards */}
                {formData.attendees.length > 0 && (
                  <div className="space-y-3">
                    <div className="text-sm font-medium subheading flex items-center">
                      {/* <UserCheck className="w-4 h-4 mr-2" /> */}
                      {formData.typeOfEvent==='invitation' && (`Selected Invitees (${formData.attendees.length}`)}
                      {formData.typeOfEvent==='appointment' && (`Selected Attendees (${formData.attendees.length}`)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getSelectedAttendees().map((attendee) => (
                        <div
                          key={attendee._id}
                          className="flex items-center space-x-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg hover:shadow-md transition-all duration-200"
                        >
                          <div className="w-10 h-10 bg-gradient-to-r from-[#004877] to-purple-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-semibold">
                              {attendee.firstName?.charAt(0)?.toUpperCase()}
                              {attendee.lastName?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {attendee.firstName} {attendee.lastName || ""}
                            </div>
                            <div className="text-sm text-gray-600 truncate">
                              {attendee.role || "Staff Member"}
                            </div>
                            {attendee.email && <div className="text-xs text-gray-500 truncate">{attendee.email}</div>}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              const newAttendees = formData.attendees.filter((id) => id !== attendee._id)
                              setFormData({ ...formData, attendees: newAttendees })
                            }}
                            className="p-1 hover:bg-red-100 rounded-full transition-colors"
                          >
                            <X className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Location */}
            <Row gutter={16} className="mt-4">
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">{formData.typeOfEvent==='visit' && "Address"} {formData.typeOfEvent==='invitation' && "Venue"} {formData.typeOfEvent==='appointment' && "Meeting Location"}</label>
                <textarea
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full create-event-textarea px-3 py-2 border border-gray-800 rounded-md text-sm"
                />
              </Col>
              <Col span={12}>
                <label className="block text-sm font-medium subheading mb-1">{formData.typeOfEvent==='visit' && "Purpose of Visit"} {formData.typeOfEvent==='invitation' && "Invitation Details"} {formData.typeOfEvent==='appointment' && "Meeting Agenda"}</label>
                <textarea
                  name="description"
                  value={formData.description}  
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full create-event-textarea px-3 py-2 border border-gray-800 rounded-md text-sm resize-none"
                />
              </Col>
            </Row>
           
          </form>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200  flex justify-between items-center">
          <div className="flex space-x-2">
            {showTimeSelector && (
              <button
                type="button"
                onClick={() => {
                  setShowTimeSelector(false);
                  setFormData(prev => ({ ...prev, startTime: '', endTime: '' }));
                }}
                className="px-4 py-2 subheading hover:bg-gray-200 rounded-md transition-colors text-sm"
              >
                Remove time
              </button>
            )}
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 subheading hover:bg-gray-200 rounded-md transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="px-6 py-2 buttonHeading"
            >
              Save
            </button>
          </div>
        </div>
      </div>
      <BlockCalendarModal
        isOpen={isBlockModalOpen}
        onClose={() => setIsBlockModalOpen(false)}
        onSave={handleBlockCalendarSave}
        createEventClose={onClose}
        initialDate={selectedDate || formData?.date}
        initialStartTime={selectedStartTime || formData?.startTime}
        initialEndTime={selectedEndTime || formData?.endTime}
      />
    </div>
  );
};

export default CreateEventModal;
